import React, { lazy } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SearchIcon, LockIcon, XIcon, PlusIcon } from 'lucide-react';
// The "chaos" beat — a believable, screenshot-style browser mockup: an
// overstuffed tab bar with 11 crammed tabs, a search/address bar, and a messy
// fan of half-buried app windows peeking out behind the frame. Built from real
// DOM/Tailwind so it reads as an actual product screenshot, not a vector
// illustration. Mirrors the calm, single-window resolution in WorkYouCantGetTo.
type Tab = {
  slug: string;
  title: string;
  active?: boolean;
};
// 11 tabs, deliberately over-packed so the bar feels claustrophobic.
const TABS: Tab[] = [{
  slug: 'gmail',
  title: 'Inbox (47)',
  active: true
}, {
  slug: 'slack',
  title: '#general'
}, {
  slug: 'notion',
  title: 'Q3 Planning'
}, {
  slug: 'salesforce',
  title: 'Acme — Deal'
}, {
  slug: 'googlesheets',
  title: 'Forecast.xlsx'
}, {
  slug: 'linear',
  title: 'ENG-2841'
}, {
  slug: 'figma',
  title: 'Dashboard v4'
}, {
  slug: 'hubspot',
  title: 'Pipeline'
}, {
  slug: 'stripe',
  title: 'Payouts'
}, {
  slug: 'googlecalendar',
  title: 'Today'
}, {
  slug: 'zoom',
  title: 'Standup'
}];
// The app windows fanned out behind the browser — partially obscured, layered
// with real micro-UI so the pile feels deep and lived-in.
function BehindWindow({
  className,
  rotate,
  z,
  accent,
  label,
  children
}: {
  className: string;
  rotate: number;
  z: number;
  accent: string;
  label: string;
  children: React.ReactNode;
}) {
  return <div className={`absolute w-[260px] rounded-2xl bg-white border border-black/5 shadow-[0_24px_60px_-24px_rgba(43,33,26,0.45)] overflow-hidden ${className}`} style={{
    transform: `rotate(${rotate}deg)`,
    zIndex: z
  }} aria-hidden="true" data-id="element-5403" data-mp="2ok3hvrxy2n76">
      {/* window chrome */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-black/[0.06] bg-[#FBFAF8]" data-id="element-5404" data-mp="184ans15oa5st">
        <span className="w-2 h-2 rounded-full bg-[#FF5F57]" data-id="element-5405" data-mp="1368uknej8q3m" />
        <span className="w-2 h-2 rounded-full bg-[#FEBC2E]" data-id="element-5406" data-mp="337gpzwl8to03" />
        <span className="w-2 h-2 rounded-full bg-[#28C840]" data-id="element-5407" data-mp="339a0jmyz8sdd" />
        <span className="ml-2 text-[9px] font-semibold text-neutral-400 truncate" data-id="element-5408" data-mp="386td8ig10x37">
          {label}
        </span>
        <span className="ml-auto w-3.5 h-3.5 rounded-[5px]" style={{
        backgroundColor: accent
      }} data-id="element-5409" data-mp="3c6ak9gb3q2ah" />
      </div>
      <div className="p-3" data-id="element-5410" data-mp="3h4c443gcq5n0">{children}</div>
    </div>;
}
function Line({
  w,
  dark
}: {
  w: string;
  dark?: boolean;
}) {
  return <div className={`h-1.5 rounded ${dark ? 'bg-neutral-300' : 'bg-neutral-100'}`} style={{
    width: w
  }} data-id="element-5411" data-mp="3r4co7rm1ops9" />;
}
export function TwelveTabProblem() {
  const reduced = !!useReducedMotion();
  return <section className="relative w-full overflow-hidden bg-[#1A1410] md:bg-[#F4F0EA] py-24 md:py-32 border-t border-neutral-200/70" data-id="element-5412" data-mp="3sbqmlblqt05">
      {/* Desktop dark panel — occupies the LEFT HALF behind the content on the
                                            dark-brown stage (the same #1A1410 used by the "Shared
                                            intelligence across your stack" beat). Kept as a FLAT solid color —
                                            no dot grid, no warm glow. The clean vertical seam where it meets the
                                            white right half is intentional — a two-tone split down the middle
                                            of the section. */}
      <div aria-hidden="true" className="hidden md:block absolute inset-y-0 left-0 w-1/2 overflow-hidden bg-[#1A1410]" data-id="element-5413" data-mp="16knzo9ffzjvz" />

      {/* Desktop calm stage — the RIGHT HALF is a clean WHITE panel (the
                                            light-side counterpart to the dark-brown left half). It carries only
                                            a subtle small-dot grid: dark, low-opacity dots on white. Masked so
                                            the dots fade softly toward the seam and the section edges, with a
                                            whisper of warm glow for a little life. The browser pile floats over
                                            it. */}
      <div aria-hidden="true" className="hidden md:block absolute inset-y-0 right-0 w-1/2 overflow-hidden bg-white" data-id="element-5414" data-mp="1a1ir2cve36oh">
        {/* Fine small-dot grid — dark, low-opacity dots on the cream surface. */}
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(rgba(43,33,26,0.10) 1px, transparent 1px)',
        backgroundSize: '16px 16px',
        maskImage: 'radial-gradient(120% 100% at 65% 45%, #000 25%, transparent 88%)',
        WebkitMaskImage: 'radial-gradient(120% 100% at 65% 45%, #000 25%, transparent 88%)'
      }} data-id="element-5415" data-mp="102b5x3iey7qt" />

        {/* Faint warm glow centred behind the browser pile. */}
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(55% 55% at 60% 42%, rgba(234,88,12,0.05) 0%, transparent 70%)'
      }} data-id="element-5416" data-mp="1j0dvbjevi6p5" />
      </div>

      {/* Mobile stage — the whole section is the dark-brown surface; a subtle
                                            small-dot grid in light dots sits behind the content, masked so the
                                            dots fade to the dark surface at every edge, with a whisper of warm
                                            glow behind the headline. */}
      <div aria-hidden="true" className="md:hidden pointer-events-none absolute inset-0 overflow-hidden" data-id="element-5417" data-mp="4tfveh9z5jr4">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(70% 50% at 50% 30%, rgba(234,88,12,0.12) 0%, transparent 70%)'
      }} data-id="element-5418" data-mp="3d99ff63ildcx" />

        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
        backgroundSize: '16px 16px',
        maskImage: 'radial-gradient(120% 85% at 50% 45%, #000 28%, transparent 86%)',
        WebkitMaskImage: 'radial-gradient(120% 85% at 50% 45%, #000 28%, transparent 86%)'
      }} data-id="element-5419" data-mp="38d2fpv1poy3f" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1280px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center" data-id="element-5420" data-mp="xiddoke6rgjv">
        {/* Text (Left) */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative z-10 max-w-xl text-left" data-id="element-5421" data-mp="19k4xhbwc2mi9">
          <h2 className="text-4xl md:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#FAF8F4]" data-id="element-5422" data-mp="lwwtcxan5jht">
            Stop paying the{' '}
            {/* Always break before "context-switch tax" so the orange phrase
             sits on its own second line at every width — including wide
             screens where the natural flow otherwise split "context-". */}
            <br data-id="element-5423" data-mp="35nfkbtu88kf" />
            <span className="whitespace-nowrap text-[#EA580C]" data-id="element-5424" data-mp="342o4v2tl5qs">
              context-switch tax
            </span>
          </h2>
          {/* On mobile the column is centered, so we keep a tighter measure and
           balance the wrap so the lines come out even instead of ragged. The
           number+unit phrases ("23 minutes", "11 tabs") are held together with
           nowrap so a line never starts or ends on a lonely number. Desktop
           keeps its natural left-aligned flow. */}
          <p className="mt-5 md:mt-6 max-w-sm md:max-w-xl text-base md:text-lg text-white/65 font-medium leading-relaxed" data-id="element-5425" data-mp="27dns95d2bicv">
            Every context switch costs your team{' '}
            <span className="whitespace-nowrap" data-id="element-5426" data-mp="1xodxfhqu3ll2">23 minutes</span> of focus. Giga
            works across your whole stack, so no one has to live in{' '}
            <span className="whitespace-nowrap" data-id="element-5427" data-mp="1tky16miofn8w">11 tabs</span>.
          </p>
        </motion.div>

        {/* Visual (Right) — realistic over-stuffed browser screenshot */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative h-[440px] md:h-[500px] w-full flex items-center justify-center" data-id="element-5428" data-mp="1joczob5v8u9t">
          {/* Behind: chat app window — tucked just up-and-left behind the front
           browser. Kept inside the visual stage (small positive left inset) so
           it never hangs out over the text column / section edge and reads as
           part of the pile rather than a clipped, floating card. */}
          <motion.div className="absolute left-2 top-2 md:left-4" animate={reduced ? undefined : {
          y: [0, -7, 0]
        }} transition={reduced ? undefined : {
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-5429" data-mp="2onqbn1l062nl">
            <BehindWindow className="" rotate={-8} z={1} accent="#8B5CF6" label="Slack — #general" data-id="element-5430" data-mp="20x42t836cecj">
              <div className="space-y-2.5" data-id="element-5431" data-mp="25w77usj5mkki">
                {[{
                w: '70%',
                who: '#EA580C'
              }, {
                w: '52%',
                who: '#3B82F6'
              }, {
                w: '64%',
                who: '#10B981'
              }].map((m, i) => <div key={i} className="flex items-start gap-2" data-id="element-5432" data-mp="1ib6aq4pmc9c6" data-mpi={i}>
                    <span className="w-5 h-5 rounded-md shrink-0" style={{
                  backgroundColor: m.who,
                  opacity: 0.85
                }} data-id="element-5433" data-mp="1wa5zrdh1mq8v" />

                    <div className="flex-1 space-y-1 pt-0.5" data-id="element-5434" data-mp="2587yfmdb16sz">
                      <Line w="38%" dark data-id="element-5435" data-mp="217o0igkwpz5s" />
                      <Line w={m.w} data-id="element-5436" data-mp="216mn45gf50kw" />
                    </div>
                  </div>)}
                <div className="mt-1 h-6 rounded-lg border border-neutral-200 bg-neutral-50" data-id="element-5437" data-mp="2ezceyqimozpx" />
              </div>
            </BehindWindow>
          </motion.div>

          {/* Behind: spreadsheet window */}
          <motion.div className="absolute right-0 top-2 md:-right-4" animate={reduced ? undefined : {
          y: [0, 8, 0]
        }} transition={reduced ? undefined : {
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.6
        }} data-id="element-5438" data-mp="2oybmu6vkkrbv">
            <BehindWindow className="" rotate={7} z={1} accent="#10B981" label="Forecast.xlsx" data-id="element-5439" data-mp="2xpxklsfir03m">
              <div className="grid grid-cols-4 gap-px bg-neutral-200 rounded overflow-hidden" data-id="element-5440" data-mp="2sqkvt2537iye">
                {Array.from({
                length: 16
              }).map((_, i) => <div key={i} className={`h-5 ${i % 4 === 0 ? 'bg-[#F4F0EA]' : i === 5 || i === 10 ? 'bg-[#EA580C]/15' : 'bg-white'} flex items-center px-1`} data-id="element-5441" data-mp="2onoedera9sw1" data-mpi={i}>
                    <span className="h-1 w-full rounded bg-neutral-200" data-id="element-5442" data-mp="3gh6va510zuws" />
                  </div>)}
              </div>
            </BehindWindow>
          </motion.div>

          {/* Behind: kanban window */}
          <motion.div className="absolute -bottom-2 left-10" animate={reduced ? undefined : {
          y: [0, 6, 0]
        }} transition={reduced ? undefined : {
          duration: 6.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1.1
        }} data-id="element-5443" data-mp="37fh2aurhwnww">
            <BehindWindow className="" rotate={4} z={1} accent="#3B82F6" label="Linear — ENG-2841" data-id="element-5444" data-mp="bjudqmule76x">
              <div className="grid grid-cols-3 gap-2" data-id="element-5445" data-mp="gixh82uxc5cs">
                {[2, 3, 1].map((n, col) => <div key={col} className="space-y-1.5" data-id="element-5446" data-mp="kj7odiugd1t8" data-mpi={col}>
                    <Line w="60%" dark data-id="element-5447" data-mp="khmuzwjvjowy" />
                    {Array.from({
                  length: n
                }).map((_, i) => <div key={i} className="rounded-md border border-neutral-200 bg-neutral-50 p-1.5 space-y-1" data-id="element-5448" data-mp="u9j5y87279il" data-mpi={i}>
                        <Line w="80%" data-id="element-5449" data-mp="z7ud2b5yz5x4" />
                        <Line w="50%" data-id="element-5450" data-mp="z6sullmmjh5d" />
                      </div>)}
                  </div>)}
              </div>
            </BehindWindow>
          </motion.div>

          {/* FRONT: the over-stuffed browser window */}
          <div className="relative z-10 w-[340px] md:w-[400px] rounded-2xl bg-white border border-black/[0.06] shadow-[0_40px_90px_-30px_rgba(43,33,26,0.55)] overflow-hidden" data-id="element-5451" data-mp="186xy3kop5ve9">
            {/* macOS toolbar */}
            <div className="flex items-center gap-2 px-3 pt-3 pb-0 bg-[#EDE9E3]" data-id="element-5452" data-mp="3a28atz7sl8ew">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57]" data-id="element-5453" data-mp="h813nyq6tydv" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" data-id="element-5454" data-mp="c7vh2wyrz22n" />
              <span className="w-3 h-3 rounded-full bg-[#28C840]" data-id="element-5455" data-mp="q6d04hdqjqe8" />
            </div>
            {/* Tab strip — crammed */}
            <div className="flex items-end gap-px px-2 pt-2 bg-[#EDE9E3] overflow-hidden" data-id="element-5456" data-mp="m3yiu1ppsyp2">
              {TABS.map(t => <div key={t.slug} className={`group/tab relative flex items-center gap-1 min-w-0 shrink rounded-t-lg pl-1.5 pr-1 py-1.5 ${t.active ? 'bg-white shadow-[0_-1px_3px_rgba(0,0,0,0.04)]' : 'bg-[#E2DDD5]'}`} style={{
              flexBasis: t.active ? '78px' : '34px'
            }} data-id="element-5457" data-mp="103hrcvmfuc1w">
                  <img src={`https://cdn.simpleicons.org/${t.slug}`} alt="" aria-hidden="true" className="w-3 h-3 object-contain shrink-0" loading="lazy" onError={e => {
                const img = e.currentTarget as HTMLImageElement;
                if (!img.dataset.fallback) {
                  img.dataset.fallback = '1';
                  img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${t.slug}.svg`;
                } else {
                  img.style.visibility = 'hidden';
                }
              }} data-id="element-5458" data-mp="14wkf2n4jara6" />

                  {t.active && <span className="text-[9px] font-semibold text-neutral-700 truncate" data-id="element-5459" data-mp="1siwaiv8qxi28">
                      {t.title}
                    </span>}
                  {t.active && <XIcon size={9} className="shrink-0 text-neutral-400" aria-hidden="true" data-id="element-5460" data-mp="1epx0v9vnai2l" />}
                </div>)}
              <span className="shrink-0 ml-0.5 mb-1 flex items-center justify-center w-4 h-4 text-neutral-400" data-id="element-5461" data-mp="1jkawnde7yr7s">
                <PlusIcon size={11} aria-hidden="true" data-id="element-5462" data-mp="14rh68wxesqoc" />
              </span>
            </div>
            {/* Address bar */}
            <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-black/[0.05]" data-id="element-5463" data-mp="vs3w021gny2k">
              <LockIcon size={11} className="text-neutral-400 shrink-0" aria-hidden="true" data-id="element-5464" data-mp="10s8g021kvaxj" />

              <div className="flex-1 flex items-center gap-1.5 h-6 rounded-full bg-[#F4F0EA] px-2.5" data-id="element-5465" data-mp="srk8jn29rivm">
                <span className="text-[10px] font-medium text-neutral-500 truncate" data-id="element-5466" data-mp="nrfsfy59wq5x">
                  mail.google.com/u/0/#inbox
                </span>
              </div>
              <SearchIcon size={12} className="text-neutral-400 shrink-0" aria-hidden="true" data-id="element-5467" data-mp="ezm5ntz5ij7w" />
            </div>
            {/* Inbox content — dense list */}
            <div className="bg-white px-2 py-2" data-id="element-5468" data-mp="a246gvaxrid3">
              <div className="flex items-center justify-between px-2 pb-2" data-id="element-5469" data-mp="51pw36lv3wbw">
                <span className="text-[11px] font-bold text-neutral-800" data-id="element-5470" data-mp="53j2ckskcejd">
                  Primary
                </span>
                <span className="text-[9px] font-bold text-white bg-[#EA580C] rounded-full px-1.5 py-0.5" data-id="element-5471" data-mp="3s6fhj9mye4pc">
                  47 unread
                </span>
              </div>
              <div className="divide-y divide-neutral-100" data-id="element-5472" data-mp="1ue1tn3jzrk13">
                {[{
                from: 'Sarah Chen',
                w: '82%',
                unread: true
              }, {
                from: 'Stripe',
                w: '64%',
                unread: true
              }, {
                from: 'GitHub',
                w: '74%',
                unread: true
              }, {
                from: 'Notion',
                w: '58%',
                unread: false
              }, {
                from: 'Acme Corp',
                w: '70%',
                unread: false
              }].map((row, i) => <div key={i} className="flex items-center gap-2.5 px-2 py-1.5" data-id="element-5473" data-mp="2ucd602sb8qia" data-mpi={i}>
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${row.unread ? 'bg-[#EA580C]' : 'bg-transparent'}`} data-id="element-5474" data-mp="2uehih949yxu6" />

                    <span className={`text-[10px] w-16 shrink-0 truncate ${row.unread ? 'font-bold text-neutral-800' : 'font-medium text-neutral-500'}`} data-id="element-5475" data-mp="33dujrei8wg3t">
                      {row.from}
                    </span>
                    <div className="flex-1" data-id="element-5476" data-mp="imauwi7t6mlt">
                      <Line w={row.w} dark={row.unread} data-id="element-5477" data-mp="ip6s24bu1797" />
                    </div>
                    <span className="text-[9px] text-neutral-400 shrink-0" data-id="element-5478" data-mp="2eonrwqd25b75">
                      {i + 1}m
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* "11 tabs open" counter chip */}
          <motion.div className="absolute bottom-4 right-2 md:right-6 z-20 flex items-center gap-2 rounded-full bg-[#2B211A] pl-2 pr-3 py-1.5 shadow-[0_16px_30px_-12px_rgba(43,33,26,0.6)]" animate={reduced ? undefined : {
          y: [0, -6, 0]
        }} transition={reduced ? undefined : {
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-5479" data-mp="2jotmofemjdtg">
            <span className="flex -space-x-1.5" data-id="element-5480" data-mp="20z3rvsav2s1j">
              {['#EA580C', '#3B82F6', '#8B5CF6'].map(c => <span key={c} className="w-3.5 h-3.5 rounded-full border border-[#2B211A]" style={{
              backgroundColor: c
            }} data-id="element-5481" data-mp="1d99e98ipwzg8" />)}
            </span>
            <span className="text-[11px] font-bold text-white" data-id="element-5482" data-mp="189wjm1lluo33">
              11 tabs open
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}